import { IClientLogger } from "./clientLogger";

type ApiResult<T> = { type: "ok"; data: T } | { type: "expired" } | { type: "error" };

interface ApiResponse<T> {
    status: string;
    data?: T;
}

export interface User {
    id: string;
    email: string;
    name: string;
    is_admin: boolean;
}

export interface StatusResponse {
    status: "clocked_in" | "clocked_out" | "never_clocked_in";
    clock_in?: string;
    clock_out?: string;
    last_note?: string;
    ms_on_clock?: number;
    is_admin: boolean;
}

export interface ReportRow {
    user_id: string;
    period: string;
    seconds: number;
    notes: string[];
}

export interface ReportResponse {
    email: string;
    period: string;
    report: ReportRow[];
}

const BACKEND_BASE = "/.netlify/functions";

export interface ITimeClock {
    clockIn(token: string): Promise<ApiResult<StatusResponse>>;
    clockOut(token: string, note: string): Promise<ApiResult<StatusResponse>>;
}

export interface IApiService {
    getStatus(token: string): Promise<ApiResult<StatusResponse>>;
    registerUser(token: string): Promise<ApiResult<string>>;
    clockIn(token: string): Promise<ApiResult<null>>;
    clockOut(token: string, note: string): Promise<ApiResult<null>>;
    getUsers(token: string): Promise<ApiResult<User[]>>;
    getUserById(token: string, userId: string): Promise<ApiResult<User>>;
    getUserByEmail(token: string, email: string): Promise<ApiResult<User>>;
    createUser(token: string, email: string, name: string, isAdmin: boolean): Promise<ApiResult<User>>;
    updateUser(token: string, user: User): Promise<ApiResult<User>>;
    deleteUser(token: string, userId: string): Promise<ApiResult<null>>;
    getReport(token: string, period: string, userEmail: string | null): Promise<ApiResult<ReportResponse>>
}

export class TimeClock implements ITimeClock {
    private apiService: IApiService;

    constructor(apiService: IApiService) {
        this.apiService = apiService;
    }

    async clockIn(token: string) {
        const clockInResponse = await this.apiService.clockIn(token);
        switch (clockInResponse.type) {
            case "expired":
            case "error":
                return clockInResponse;
            default:
                const getStatusResponse = await this.apiService.getStatus(token);
                switch (getStatusResponse.type) {
                    case "expired":
                    case "error":
                        return getStatusResponse;
                }

                return getStatusResponse;
        }
    }

    async clockOut(token: string, note: string) {
        const clockOutResponse = await this.apiService.clockOut(token, note);
        switch (clockOutResponse.type) {
            case "expired":
            case "error":
                return clockOutResponse;
            default:
                const getStatusResponse = await this.apiService.getStatus(token);
                switch (getStatusResponse.type) {
                    case "expired":
                    case "error":
                        return getStatusResponse;
                }

                return getStatusResponse;
        }
    }
}

export class ApiService implements IApiService {
    private _logger: IClientLogger;

    constructor(logger: IClientLogger) {
        this._logger = logger;
    }

    private async getAndLog(url: string, token: string) {
        this._logger.logDebug(`GET from ${url}`);
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        this._logger.logDebug(`GET from ${url} result: ${response.status}`);
        if (response.ok) {
            this._logger.logDebug(`GET from ${url} result: ${response.status}`);
        } else {
            const msg = await response.text();
            this._logger.logDebug(`GET from ${url} result: ${response.status} - ${msg}`);
        }
        return response;
    }

    private async verbAndLog(verb: string, url: string, token: string, body?: any) {
        this._logger.logDebug(`${verb} to ${url}`);
        const response = await fetch(url, {
            method: verb,
            body: body ? JSON.stringify(body) : undefined,
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });
        this._logger.logDebug(`${verb} to ${url} result: ${response.status}`);
        if (response.ok) {
            this._logger.logDebug(`${verb} to ${url} result: ${response.status}`);
        } else {
            const msg = await response.text();
            this._logger.logDebug(`${verb} to ${url} result: ${response.status} - ${msg}`);
        }
        return response;
    }

    private postAndLog(url: string, token: string, postBody?: any) {
        return this.verbAndLog("POST", url, token, postBody);
    }

    private putAndLog(url: string, token: string, postBody?: any) {
        return this.verbAndLog("PUT", url, token, postBody);
    }

    private deleteAndLog(url: string, token: string, postBody?: any) {
        return this.verbAndLog("DELETE", url, token, postBody);
    }

    async getUsers(token: string): Promise<ApiResult<User[]>> {
        const response = await this.getAndLog(`${BACKEND_BASE}/get-users`, token);
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }
        const data = (await response.json()) as ApiResponse<User[]>;
        if (data.status !== "ok" || !data.data) {
            return { type: "error" };
        }
        return { type: "ok", data: data.data };
    }

    async getUserById(token: string, userId: string): Promise<ApiResult<User>> {
        const response = await this.getAndLog(`${BACKEND_BASE}/get-user/${userId}`, token);
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }
        return { type: "ok", data: ((await response.json()).data) as User };
    }

    async getUserByEmail(token: string, email: string): Promise<ApiResult<User>> {
        const response = await this.getAndLog(`${BACKEND_BASE}/get-user-by-email/${email}`, token);
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }
        return { type: "ok", data: ((await response.json()).data) as User };
    }

    async createUser(token: string, email: string, name: string, isAdmin: boolean): Promise<ApiResult<User>> {
        const response = await this.postAndLog(`${BACKEND_BASE}/new-user`, token, { email, name, is_admin: isAdmin });
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }
        const data = (await response.json()) as ApiResponse<User>;
        if (data.status !== "created" || !data.data) {
            return { type: "error" };
        }
        return { type: "ok", data: data.data };
    }

    async updateUser(token: string, user: User): Promise<ApiResult<User>> {
        const response = await this.putAndLog(`${BACKEND_BASE}/put-user/${user.id}`, token, {
            email: user.email,
            name: user.name,
            is_admin: user.is_admin,
        });
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }
        const data = (await response.json()) as ApiResponse<User>;
        if (data.status !== "updated" || !data.data) {
            return { type: "error" };
        }
        return { type: "ok", data: data.data };
    }

    async deleteUser(token: string, userId: string): Promise<ApiResult<null>> {
        const response = await this.deleteAndLog(`${BACKEND_BASE}/delete-user/${userId}`, token);
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }
        return { type: "ok", data: null };
    }

    async getStatus(token: string): Promise<ApiResult<StatusResponse>> {
        const response = await this.getAndLog(`${BACKEND_BASE}/get-status`, token);
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }
        const data = await response.json();
        if (typeof data.status !== "string") {
            return { type: "error" };
        }
        return { type: "ok", data: data };
    }

    async registerUser(token: string): Promise<ApiResult<string>> {
        const response = await this.postAndLog(`${BACKEND_BASE}/user-register`, token);
        if (!response.ok) {
            return { type: "error" };
        }
        switch (response.status) {
            case 200:
                return { type: "ok", data: "existing" };
            case 201:
                return { type: "ok", data: "new" };
            default:
                throw new Error("Unexpected response");
        }
    }

    async clockIn(token: string): Promise<ApiResult<null>> {
        const response = await this.postAndLog(`${BACKEND_BASE}/clock-in`, token);
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }
        return { type: "ok", data: null };
    }

    async clockOut(token: string, note: string): Promise<ApiResult<null>> {
        const response = await this.postAndLog(`${BACKEND_BASE}/clock-out`, token, { note: note });
        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }

        return { type: "ok", data: null };
    }

    async getReport(token: string, period: string, userEmail: string | null): Promise<ApiResult<ReportResponse>> {
        const response = await fetch("/.netlify/functions/get-report", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...(userEmail ? { user_email: userEmail } : {}),
                period,
            }),
        });

        if (response.status === 401) {
            return { type: "expired" };
        }
        if (!response.ok) {
            return { type: "error" };
        }

        const data = await response.json();
        return { type: "ok", data: data };
    }
}
